import React, { useState } from 'react';
import { MdSearch, MdContentCopy, MdExtension, MdInfoOutline, MdLaunch, MdAutoFixHigh, MdCheck } from 'react-icons/md';

const RECOMMENDED_EXTENSIONS = [
  {
    name: 'SelectorsHub',
    desc: 'The most advanced XPath & CSS selector tool. Handles Shadow DOM, iframes, and dynamic elements.',
    url: 'https://selectorshub.com/',
    icon: '🚀'
  },
  {
    name: 'XPath Helper',
    desc: 'Simple and clean extension to edit, view and pick relevant XPath queries on any webpage.',
    url: 'https://chrome.google.com/webstore/detail/xpath-helper/hgbbnnohmjbcljjnclbmhciedbknoide',
    icon: '🔍'
  },
  {
    name: 'SelectorsHub Pro',
    desc: 'Premium version with even more advanced locator strategies and automation features.',
    url: 'https://selectorshub.com/selectorshub-pro/',
    icon: '💎'
  }
];

const XPATH_GUIDE_STEPS = [
  {
    title: 'Open Inspector',
    desc: 'Right-click any element on a webpage and select "Inspect" to open the Developer Tools.',
    color: '#3b82f6'
  },
  {
    title: 'Locate Element',
    desc: 'In the "Elements" tab, the code for the selected element will be highlighted.',
    color: '#10b981'
  },
  {
    title: 'Copy XPath',
    desc: 'Right-click the highlighted code, go to "Copy", and select "Copy XPath".',
    color: '#f59e0b'
  }
];

function Addons() {
  const [htmlSnippet, setHtmlSnippet] = useState('<div class="container">\n  <label for="username">Username:</label>\n  <input id="username" name="user_login" placeholder="Enter username" />\n  <button type="submit" class="btn-primary">Login Now</button>\n  <a href="/forgot">Forgot Password?</a>\n</div>');
  const [xpath, setXpath] = useState('//button[text()="Login Now"]');
  const [testResult, setTestResult] = useState(null);
  const [generatedXpaths, setGeneratedXpaths] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const testXpath = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlSnippet, 'text/html');
      const result = doc.evaluate(xpath, doc, null, XPathResult.ANY_TYPE, null);
      
      let node = result.iterateNext();
      if (node) {
        setTestResult({
          success: true,
          message: 'Match Found!',
          content: node.outerHTML || node.textContent
        });
      } else {
        setTestResult({
          success: false,
          message: 'No matches found.'
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: 'Invalid XPath or HTML: ' + err.message
      });
    }
  };

  const getSmartXPath = (el) => {
    if (el.id) return `//*[@id="${el.id}"]`;
    if (el.getAttribute('name')) return `//${el.tagName.toLowerCase()}[@name="${el.getAttribute('name')}"]`;
    if (el.getAttribute('placeholder')) return `//${el.tagName.toLowerCase()}[@placeholder="${el.getAttribute('placeholder')}"]`;
    
    const text = el.textContent.trim();
    if (text && text.length < 30) {
      // Escape single quotes if necessary, simplified for this tool
      const escapedText = text.replace(/"/g, "'");
      return `//${el.tagName.toLowerCase()}[text()="${escapedText}"]`;
    }

    // Fallback to basic relative path
    let path = '';
    for (let current = el; current && current.nodeType === 1; current = current.parentNode) {
      let index = 1;
      for (let sibling = current.previousSibling; sibling; sibling = sibling.previousSibling) {
        if (sibling.nodeType === 1 && sibling.tagName === current.tagName) {
          index++;
        }
      }
      const tagName = current.tagName.toLowerCase();
      path = `/${tagName}${(index > 1 || (current.nextSibling && current.nextSibling.nodeType === 1)) ? `[${index}]` : ''}${path}`;
      if (tagName === 'body' || tagName === 'html') break;
    }
    return `/${path}`;
  };

  const generateSmartXPaths = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlSnippet, 'text/html');
      const interactiveElements = doc.querySelectorAll('button, input, select, textarea, a, label');
      
      const results = Array.from(interactiveElements).map((el, index) => {
        const tagName = el.tagName.toLowerCase();
        const type = el.getAttribute('type') || '';
        const identifier = el.id || el.getAttribute('name') || el.textContent.trim().substring(0, 20) || `Element ${index + 1}`;
        
        return {
          id: index,
          tag: tagName,
          type: type,
          displayName: `${tagName.toUpperCase()}${type ? ` (${type})` : ''}: ${identifier}`,
          xpath: getSmartXPath(el)
        };
      });
      
      setGeneratedXpaths(results);
    } catch (err) {
      setGeneratedXpaths([]);
      alert('Error generating XPaths: ' + err.message);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleTryInTester = (text) => {
    setXpath(text);
    setTestResult(null);
  };

  return (
    <div className="addons-page">
      <div className="card" style={{ marginBottom: '24px', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px' }}>
          <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', padding: '10px', borderRadius: '12px' }}>
            <MdInfoOutline color="var(--primary)" size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Pro Tip: Finding XPaths from Inspect Menu</h2>
            <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
              The quickest way to get an XPath is directly from your browser's developer tools.
            </p>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.05)' }}>
          {XPATH_GUIDE_STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ 
                minWidth: '28px', height: '28px', borderRadius: '50%', 
                backgroundColor: step.color, color: 'white', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                fontWeight: 'bold', fontSize: '14px' 
              }}>
                {i + 1}
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px 0' }}>{step.title}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid">
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <MdAutoFixHigh color="var(--primary)" size={20} />
            <h3 style={{ margin: 0 }}>Smart XPath Creator</h3>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Paste an HTML snippet below and we'll automatically generate robust XPaths for all interactive elements.
          </p>
          
          <div className="form-group">
            <label>HTML Content</label>
            <textarea 
              value={htmlSnippet}
              onChange={(e) => setHtmlSnippet(e.target.value)}
              style={{ height: '140px', fontFamily: '"Fira Code", monospace', fontSize: '12px' }}
              placeholder="Paste HTML here..."
            />
          </div>
          
          <button 
            className="btn btn-success" 
            style={{ width: '100%', padding: '12px', fontWeight: 'bold' }}
            onClick={generateSmartXPaths}
          >
            Generate Smart XPaths
          </button>

          {generatedXpaths.length > 0 && (
            <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>Identified Elements ({generatedXpaths.length})</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '400px', overflowY: 'auto', paddingRight: '4px' }}>
                {generatedXpaths.map((res) => (
                  <div key={res.id} style={{ 
                    padding: '12px', borderRadius: '8px', background: 'var(--bg-input)', 
                    border: '1px solid var(--border)', fontSize: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 'bold', color: 'var(--primary)' }}>{res.displayName}</span>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <button 
                          className="btn btn-sm btn-primary" 
                          onClick={() => handleTryInTester(res.xpath)}
                          style={{ fontSize: '11px', fontWeight: '600', height: '28px' }}
                        >
                          Try in Tester
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => copyToClipboard(res.xpath, res.id)}
                          style={{ 
                            fontSize: '11px', 
                            fontWeight: '600', 
                            height: '28px',
                            color: copiedId === res.id ? 'var(--success)' : 'var(--text-main)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {copiedId === res.id ? <><MdCheck size={14} /> Copied!</> : 'Copy XPath'}
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <code style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.2)', padding: '8px 10px', borderRadius: '6px', overflowX: 'auto', border: '1px solid var(--border)' }}>
                        {res.xpath}
                      </code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <MdSearch color="var(--primary)" size={20} />
              <h3 style={{ margin: 0 }}>Live XPath Tester</h3>
            </div>
            
            <div className="form-group">
              <label>XPath Expression</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={xpath}
                  onChange={(e) => setXpath(e.target.value)}
                  placeholder="//tag[@attr='value']"
                  style={{ fontFamily: '"Fira Code", monospace' }}
                />
                <button className="btn btn-primary" onClick={testXpath}>Validate</button>
              </div>
            </div>

            {testResult && (
              <div style={{ 
                marginTop: '16px', padding: '12px', borderRadius: '8px', 
                backgroundColor: testResult.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${testResult.success ? 'var(--success)' : 'var(--danger)'}`
              }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: testResult.success ? 'var(--success)' : 'var(--danger)', marginBottom: '4px' }}>
                  {testResult.message}
                </div>
                {testResult.content && (
                  <pre style={{ margin: 0, fontSize: '11px', overflowX: 'auto', padding: '8px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
                    {testResult.content}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
          <MdExtension color="var(--primary)" size={20} />
          <h3 style={{ margin: 0 }}>Curated Browser Extensions</h3>
        </div>
        <div className="responsive-grid-4" style={{ gap: '16px' }}>
          {RECOMMENDED_EXTENSIONS.map((ext, i) => (
            <div key={i} className="addon-item" style={{ 
              padding: '16px', borderRadius: '12px', background: 'var(--bg-input)', 
              border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', textAlign: 'center'
            }}>
              <span style={{ fontSize: '32px' }}>{ext.icon}</span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', wordBreak: 'break-all' }}>{ext.name}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>{ext.desc}</p>
                <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
                  <a href={ext.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <MdLaunch /> Visit Site
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Addons;
